import React, { useEffect, useState } from "react";
import { IconButton, MessageBar, MessageBarType } from "@fluentui/react";
import SearchBox from "@/ui-controls/SearchBox";
import {
  Customer,
  CustomerStatusFilter,
  CustomerType,
  getCustomers,
} from "@/api/customers";
import ShimmerList from "@/ui-controls/ShimmerList";
import CustomerFilterPanel from "@/components/customers/CustomerFilterPanel";
import CustomerDetailList from "@/components/customers/CustomerDetailList";
import NewCustomer from "@/components/customers/NewCustomer";

const mapCustomerTypesToIds = (types: string[]): number[] => {
  return types
    .map((type) => {
      switch (type) {
        case "Carrier":
          return CustomerType.Carrier;
        case "Shipper":
          return CustomerType.Shipper;
        case "System":
          return CustomerType.System;
        default:
          return undefined;
      }
    })
    .filter((id): id is number => id !== undefined);
};

const CustomersPage = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCustomerTypes, setSelectedCustomerTypes] = useState<string[]>(
    []
  );
  const [appliedCustomerTypes, setAppliedCustomerTypes] = useState<string[]>(
    []
  );
  const [selectedStatusFilter, setSelectedStatusFilter] =
    useState<CustomerStatusFilter>("active");
  const [appliedStatusFilter, setAppliedStatusFilter] =
    useState<CustomerStatusFilter>("active");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    getAllCustomers(appliedStatusFilter, appliedCustomerTypes);
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const getAllCustomers = (
    status: CustomerStatusFilter,
    customerTypes: string[] = []
  ) => {
    setLoading(true);

    const isActive = status === "active";
    const customerTypeIds = mapCustomerTypesToIds(customerTypes);

    getCustomers(isActive, customerTypeIds)
      .then((result) => {
        setCustomers(result);
        setLoading(false);
      })
      .catch((error) => {
        handleError(error);
        setLoading(false);
      });
  };

  const handleError = (error: any) => {
    // Error handling would go here in real app
  };

  const handleRefresh = () => {
    setSearchTerm("");
    setSelectedCustomerTypes([]);
    setAppliedCustomerTypes([]);
    setSelectedStatusFilter("active");
    setAppliedStatusFilter("active");
    getAllCustomers("active", []);
  };

  const handleCustomerTypeFilter = (selectedTypes: string[]) => {
    setSelectedCustomerTypes(selectedTypes);
  };

  const handleStatusFilterChange = (status: CustomerStatusFilter) => {
    setSelectedStatusFilter(status);
  };

  const handleApplyFilters = () => {
    setAppliedCustomerTypes(selectedCustomerTypes);
    setAppliedStatusFilter(selectedStatusFilter);
    getAllCustomers(selectedStatusFilter, selectedCustomerTypes);
  };

  const handleClearFilters = () => {
    setSelectedCustomerTypes([]);
    setAppliedCustomerTypes([]);
    setSelectedStatusFilter("active");
    setAppliedStatusFilter("active");
    getAllCustomers("active", []);
  };

  const openFilterPanel = () => {
    setIsFilterPanelOpen(true);
  };

  const closeFilterPanel = () => {
    setIsFilterPanelOpen(false);
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
  };

  const handleShowSuccess = (message: string) => {
    setSuccessMessage(message);
    setErrorMessage(null);
  };

  const handleShowError = (message: string) => {
    setErrorMessage(message);
    setSuccessMessage(null);
  };

  useEffect(() => {
    const applyFilters = () => {
      let filtered = customers;

      if (searchTerm.trim()) {
        filtered = filtered.filter(
          (customer) =>
            customer.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (customer.Description &&
              customer.Description.toLowerCase().includes(
                searchTerm.toLowerCase()
              )) ||
            (customer.CustomerNumber &&
              customer.CustomerNumber.toString().includes(searchTerm)) ||
            (customer.EntityId &&
              customer.EntityId.toString().includes(searchTerm))
        );
      }

      if (appliedCustomerTypes.length > 0) {
        filtered = filtered.filter((customer) =>
          appliedCustomerTypes.includes(customer.CustomerType)
        );
      }

      setFilteredCustomers(filtered);
    };

    applyFilters();
  }, [customers, searchTerm, appliedCustomerTypes]);

  const hasStatusFilterApplied = appliedStatusFilter !== "active";
  const hasTypeFilterApplied = appliedCustomerTypes.length > 0;
  const hasFiltersApplied = hasStatusFilterApplied || hasTypeFilterApplied;
  const totalFiltersApplied =
    (hasStatusFilterApplied ? 1 : 0) + appliedCustomerTypes.length;

  return (
    <>
      <div className="page-header">
        <h2 className="page-title">Customers</h2>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <NewCustomer
            disabled={loading}
            onSave={(customer) => setCustomers((prev) => [...prev, customer])}
          />
          <SearchBox
            placeholder="Search"
            onSearch={handleSearch}
            disabled={loading}
          />
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              iconProps={{ iconName: "Filter" }}
              title="Filter customers"
              ariaLabel="Filter customers"
              onClick={openFilterPanel}
              disabled={loading}
              styles={{
                root: {
                  width: 40,
                  height: 40,
                  backgroundColor: hasFiltersApplied ? "#deecf9" : "transparent",
                },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: {
                  fontSize: 18,
                  color:
                    hasFiltersApplied
                      ? "#0078d4"
                      : loading
                      ? "#a19f9d"
                      : "#0078d4",
                },
                rootPressed: { backgroundColor: "#e1dfdd" },
                rootDisabled: { backgroundColor: "transparent" },
              }}
            />
            {hasFiltersApplied && totalFiltersApplied > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  backgroundColor: "#d13438",
                  color: "white",
                  borderRadius: "50%",
                  width: "16px",
                  height: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "600",
                  zIndex: 1,
                }}
              >
                {totalFiltersApplied}
              </div>
            )}
          </div>
          <IconButton
            iconProps={{ iconName: "Refresh" }}
            title="Refresh"
            ariaLabel="Refresh"
            onClick={handleRefresh}
            disabled={loading}
            styles={{
              root: { width: 40, height: 40 },
              rootHovered: { backgroundColor: "#f3f2f1" },
              icon: {
                fontSize: 18,
                color: loading ? "#a19f9d" : "#0078d4",
              },
              rootPressed: { backgroundColor: "#e1dfdd" },
              rootDisabled: { backgroundColor: "transparent" },
            }}
          />
        </div>
      </div>

      {!loading ? (
        <CustomerDetailList
          CustomerList={
            searchTerm.trim() ||
            appliedCustomerTypes.length > 0 ||
            hasStatusFilterApplied
              ? filteredCustomers
              : customers
          }
          onDelete={(customer) => {
            getAllCustomers(appliedStatusFilter, appliedCustomerTypes);
          }}
          onUpdate={(customer) => {
            getAllCustomers(appliedStatusFilter, appliedCustomerTypes);
          }}
          onShowSuccess={handleShowSuccess}
          onShowError={handleShowError}
        />
      ) : (
        <ShimmerList numberOfColumns={8} />
      )}

      <CustomerFilterPanel
        isOpen={isFilterPanelOpen}
        onDismiss={closeFilterPanel}
        selectedCustomerTypes={selectedCustomerTypes}
        onCustomerTypeChange={handleCustomerTypeFilter}
        selectedStatus={selectedStatusFilter}
        onStatusChange={handleStatusFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />

      {successMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          isMultiline={false}
          onDismiss={() => setSuccessMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1001,
            },
          }}
        >
          {successMessage}
        </MessageBar>
      )}

      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={true}
          onDismiss={() => setErrorMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1001,
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default CustomersPage;
